import { ActionReducerMapBuilder } from '@reduxjs/toolkit';

import { SubscriptionSummary } from '../../../types/subscription';
import { demoApi } from '../../services/demoApi';
import { enterDemo, exitDemo } from '../demo';
import { SubscriptionState } from '../subscription';

/**
 * Demo-mode reducer cases for the `subscription` slice.
 *
 * Pulled out of `subscription.ts` so that file is free of demo awareness —
 * the slice only calls this one helper inside its `extraReducers`. Covers:
 *
 *  - `enterDemo` / `exitDemo` resets (wipe the map so the previous mode's
 *    subscriptions can't leak across the transition).
 *  - The three RTK Query matchers that mirror the real-mode flow against
 *    `demoApi.endpoints` (`pending` / `fulfilled` of summaries + `fulfilled`
 *    of per-sub details).
 */
export const applyDemoCasesToSubscriptionBuilder = (
  builder: ActionReducerMapBuilder<SubscriptionState>,
) => {
  // Flipping demo on/off wipes the subscription map so a previous
  // logged-in user's subs (or a previous demo session) can't leak into the
  // other mode while the new summary loads.
  const wipe = (state: SubscriptionState) => {
    state.subscriptions = {};
    state.activeSubscriptionId = null;
    state.activeClusterId = null;
    state.isSummaryLoading = true;
    state.isSubscriptionLoading = true;
  };
  builder.addCase(enterDemo, wipe);
  builder.addCase(exitDemo, wipe);

  builder.addMatcher(
    demoApi.endpoints.getDemoSubscriptionSummaries.matchPending,
    (state) => {
      state.isSummaryLoading = true;
    },
  );

  builder.addMatcher(
    demoApi.endpoints.getDemoSubscriptionSummaries.matchFulfilled,
    (state, { payload }) => {
      const subscriptionSummaries = payload.subscriptionSummary;
      state.isSummaryLoading = false;

      if (!subscriptionSummaries?.length) {
        state.subscriptions = {};
        return;
      }

      state.subscriptions = subscriptionSummaries.reduce(
        (acc, curr) => ({
          ...acc,
          [curr.id]: {
            ...state.subscriptions[curr.id],
            ...curr,
            profits: {
              ...curr.profits,
              noAlgorithmicProgressionProfit:
                curr.profits?.noAlgorithmicProgressionProfit ||
                state.subscriptions[curr.id]?.profits
                  ?.noAlgorithmicProgressionProfit,
            },
          },
        }),
        {},
      );

      const highestValueSubscription =
        subscriptionSummaries.reduce<SubscriptionSummary | null>(
          (max, curr) =>
            curr.totalBalanceInStable > (max?.totalBalanceInStable || -Infinity)
              ? curr
              : max,
          null,
        );

      if (highestValueSubscription?.id && !state.activeSubscriptionId) {
        state.activeSubscriptionId = highestValueSubscription.id;
      }
    },
  );

  builder.addMatcher(
    demoApi.endpoints.getDemoSubscriptionDetails.matchFulfilled,
    (state, { payload }) => {
      const detail = payload?.subscriptionDetails;
      if (!detail) return;
      const dp = detail.chartData?.dataPoints;
      const subscriptionData = {
        ...detail,
        chartData: dp
          ? {
              dataPoints: {
                noAlgorithmicProgression: dp.noAlgorithmicProgression ?? [],
                total: {
                  stable: dp.total?.stable ?? [],
                },
              },
            }
          : undefined,
      };

      state.subscriptions[detail.id] = subscriptionData;
      state.isSubscriptionLoading = false;
    },
  );
};
